import React, { useState, useEffect } from "react";
import { Modal, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import OtpInput from "react-otp-input";
import {
    FaShieldAlt,
    FaLock,
    FaCheckCircle,
    FaSpinner,
    FaArrowLeft,
    FaExclamationCircle,
    FaRedoAlt,
    FaEnvelope,
    FaTimes,
    FaCheck
} from "react-icons/fa";
import { sendOTP, verifyOTP } from "../../../services/restapiService";
import { encodeJourneyId } from "../../../utils/url";
import { startSessionTimer } from "../../../utils/sessionManager";
/**
 * PaymentLogin React Bootstrap Modal Component
 * Modern authentication modal for travel booking website GoSehari
 *
 * @param {Object} props
 * @param {boolean} props.show - Modal visibility state
 * @param {Function} props.onHide - Callback to close modal
 * @param {string|number} props.journeyId - Travel booking journey ID
 */
export default function PaymentLogin({ show, onHide, journeyId }) {
    const navigate = useNavigate();

    // Step state: 1 = Email Input, 2 = OTP Verification
    const [step, setStep] = useState(1);
    const [email, setEmail] = useState("");
    const [otp, setOtp] = useState("");

    // Async status states
    const [loading, setLoading] = useState(false);
    const [resending, setResending] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    // Error state: { type: 'Invalid Email' | 'OTP Expired' | 'Wrong OTP' | 'API Failure', message: string }
    const [error, setError] = useState(null);

    // Timer state: 5 minute countdown (300 seconds)
    const [timeLeft, setTimeLeft] = useState(300);

    // Reset modal state when opened or closed
    useEffect(() => {
        if (show) {
            setStep(1);
            setOtp("");
            setError(null);
            setIsSuccess(false);
            setTimeLeft(300);
        }
    }, [show]);

    // Countdown timer logic
    useEffect(() => {
        if (!show || step !== 2 || timeLeft <= 0 || isSuccess) return;

        const timer = setInterval(() => {
            setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
        }, 1000);

        return () => clearInterval(timer);
    }, [show, step, timeLeft, isSuccess]);

    // Format seconds to MM:SS
    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    };

    // Validate email regex
    const validateEmail = (emailStr) => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(emailStr);
    };

    // Step 1 Submit: Send OTP
    const handleSendOTP = async (e) => {
        if (e) e.preventDefault();
        setError(null);

        if (!email || !email.trim()) {
            setError({
                type: "Invalid Email",
                message: "Email Address cannot be empty. Please enter your email."
            });
            return;
        }

        if (!validateEmail(email.trim())) {
            setError({
                type: "Invalid Email",
                message: "Please enter a valid email address (e.g. user@example.com)."
            });
            return;
        }

        try {
            setLoading(true);
            await sendOTP({
                name: "Guest",
                email: email.trim()
            });

            // Transition to Step 2
            setStep(2);
            setTimeLeft(300);
            setOtp("");
        } catch (err) {
            const errorMsg = err?.response?.data?.message || err?.message || "";
            setError({
                type: "API Failure",
                message: errorMsg || "Unable to send OTP. Please check your internet connection and try again."
            });
        } finally {
            setLoading(false);
        }
    };

    // Step 2 Submit: Verify OTP
    const handleVerifyOTP = async (e) => {
        if (e) e.preventDefault();
        setError(null);

        if (!otp || otp.length < 6) {
            setError({
                type: "Wrong OTP",
                message: "Please enter the complete 6-digit verification code."
            });
            return;
        }

        if (timeLeft <= 0) {
            setError({
                type: "OTP Expired",
                message: "Your verification code has expired. Please click 'Resend OTP' below."
            });
            return;
        }

        try {
            setLoading(true);
            const response = await verifyOTP({
                email,
                otp,
                journeyId
            });
            if (response?.data) {
                setIsSuccess(true);

                localStorage.setItem("token", response.data.token);
               localStorage.setItem("userEmail", response.data.user.email);
                localStorage.setItem("isVerified", "true");

                startSessionTimer();

                window.dispatchEvent(new Event("storage"));

                setTimeout(() => {
                    if (onHide) onHide();

                    navigate(`/secure-checkout/${encodeJourneyId(journeyId)}`, {
                        replace: true,
                    });

                }, 1000);
            }
        } catch (err) {
            const errorMsg = err?.response?.data?.message || err?.message || "";
            let errorType = "Wrong OTP";
            if (
                errorMsg.toLowerCase().includes("expire") ||
                err?.message === "OTP Expired"
            ) {
                errorType = "OTP Expired";
            } else if (
                errorMsg.toLowerCase().includes("server") ||
                errorMsg.toLowerCase().includes("network") ||
                errorMsg.toLowerCase().includes("api")
            ) {
                errorType = "API Failure";
            }

            setError({
                type: errorType,
                message: errorMsg || "Invalid OTP entered. Please verify the numbers and try again."
            });
        } finally {
            setLoading(false);
        }
    };

    // Resend OTP handler
    const handleResendOTP = async () => {
        setError(null);
        try {
            setResending(true);
            await sendOTP({
                name: "Guest",
                email: email.trim()
            });

            setOtp("");
            setTimeLeft(300);
        } catch (err) {
            const errorMsg = err?.response?.data?.message || err?.message || "";
            setError({
                type: "API Failure",
                message: errorMsg || "Failed to resend OTP. Please try again."
            });
        } finally {
            setResending(false);
        }
    };

    // Handle ESC key or modal backdrop close
    const handleModalClose = () => {
        if (!loading && !isSuccess && onHide) {
            onHide();
        }
    };

    return (
        <Modal
            show={show}
            onHide={handleModalClose}
            centered
            backdrop="static"
            keyboard={!loading && !isSuccess}
            dialogClassName="gosehari-payment-modal"
            contentClassName="border-0 rounded-[28px] overflow-hidden shadow-2xl bg-white/95 backdrop-blur-xl transition-all duration-300"
            aria-labelledby="secure-checkout-modal-title"
        >
            {/* Gradient Header */}
            <Modal.Header
                className="bg-black text-white border-0 p-6 sm:p-7 relative select-none"
                closeButton={false}
            >
                <div className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-3.5">
                        <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur-md flex items-center justify-center shadow-inner border border-white/20 text-white">
                            <FaShieldAlt className="text-amber-300 drop-shadow" />
                        </div>
                        <div>
                            <Modal.Title
                                id="secure-checkout-modal-title"
                                className="font-bold tracking-tight text-white mb-0.5 flex items-center gap-2"
                            >
                                <span>Secure Checkout</span>
                                <FaLock className="text-teal-200 opacity-80" />
                            </Modal.Title>
                            <p className="text-teal-50 font-normal m-0 opacity-95">
                                Verify your email to continue your booking.
                            </p>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={handleModalClose}
                        disabled={loading || isSuccess}
                        aria-label="Close modal"
                        className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/25 active:bg-white/30 text-white flex items-center justify-center transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-white/50"
                    >
                        <FaTimes className="text-sm" />
                    </button>
                </div>

                {/* Decorative subtle header glow */}
                <div className="absolute -bottom-1 left-0 right-0 h-1 bg-gradient-to-r from-teal-400 via-amber-300 to-teal-500 opacity-60"></div>
            </Modal.Header>

            {/* Modal Body */}
            <Modal.Body className="p-6 sm:p-8 bg-white text-slate-800 relative min-h-[340px] flex flex-col justify-center">
                <AnimatePresence mode="wait">
                    {/* SUCCESS STATE */}
                    {isSuccess ? (
                        <motion.div
                            key="success-view"
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            transition={{ duration: 0.4, ease: "easeOut" }}
                            className="py-8 flex flex-col items-center justify-center text-center my-auto"
                            role="alert"
                            aria-live="polite"
                        >
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: [0, 1.2, 1] }}
                                transition={{ duration: 0.5, delay: 0.1 }}
                                className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mb-5 text-emerald-600 shadow-lg shadow-emerald-100/50"
                            >
                                <FaCheckCircle className="text-5xl text-emerald-600" />
                            </motion.div>

                            <h3 className="text-2xl font-bold text-slate-900 mb-2">
                                Verification Successful
                            </h3>
                            <p className="text-sm text-slate-500 max-w-xs mx-auto mb-6">
                                Your email <span className="font-semibold text-slate-700">{email}</span> has been verified. Redirecting to GoSehari checkout...
                            </p>

                            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#0f766e] bg-teal-50 px-4 py-2 rounded-full animate-pulse">
                                <FaSpinner className="animate-spin text-sm" />
                                <span>Preparing Payment Gateway...</span>
                            </div>
                        </motion.div>
                    ) : step === 1 ? (
                        /* STEP 1: EMAIL INPUT */
                        <motion.div
                            key="step-1-email"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.3 }}
                            className="w-full my-auto"
                        >
                            {/* Bootstrap Alerts for Error Handling */}
                            {error && (
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="mb-5"
                                >
                                    <Alert
                                        variant="danger"
                                        onClose={() => setError(null)}
                                        dismissible
                                        className="rounded-2xl border border-red-200 bg-red-50 text-red-900 p-4 shadow-sm"
                                    >
                                        <div className="flex items-start gap-3 pr-2">
                                            <FaExclamationCircle className="text-red-600 mt-0.5 text-lg flex-shrink-0" />
                                            <div>
                                                <div className="font-bold text-sm">{error.type}</div>
                                                <div className="text-xs sm:text-sm mt-0.5 text-red-700">
                                                    {error.message}
                                                </div>
                                            </div>
                                        </div>
                                    </Alert>
                                </motion.div>
                            )}

                            <form onSubmit={handleSendOTP} className="space-y-6">
                                <div>
                                    <label
                                        htmlFor="checkout-email-input"
                                        className="block text-sm font-semibold text-slate-700 mb-2"
                                    >
                                        Email Address
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400">
                                            <FaEnvelope className="text-lg text-slate-400" />
                                        </div>
                                        <input
                                            id="checkout-email-input"
                                            type="email"
                                            required
                                            autoFocus
                                            disabled={loading}
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="name@example.com"
                                            aria-label="Email Address"
                                            className="w-full pl-11 pr-4 py-3.5 text-base text-slate-900 bg-slate-50 border-2 border-slate-200 rounded-2xl focus:bg-white focus:border-[#0f766e] focus:ring-4 focus:ring-teal-500/15 focus:outline-none transition-all duration-200 placeholder:text-slate-400 font-medium"
                                        />
                                    </div>
                                    <p className="mt-2 text-xs text-slate-500 flex items-center gap-1.5">
                                        <FaCheck className="text-emerald-600 text-xs flex-shrink-0" />
                                        <span>We will send a 6-digit verification code to this address.</span>
                                    </p>
                                </div>

                                <motion.button
                                    type="submit"
                                    disabled={loading}
                                    whileHover={loading ? {} : { scale: 1.015 }}
                                    whileTap={loading ? {} : { scale: 0.985 }}
                                    className="w-full py-4 px-6 rounded-2xl bg-black hover:black text-white font-bold text-base shadow-lg shadow-teal-700/25 transition-all duration-200 flex items-center justify-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-4 focus:ring-teal-500/30"
                                >
                                    {loading ? (
                                        <>
                                            <FaSpinner className="animate-spin text-xl text-white" />
                                            <span>Sending OTP...</span>
                                        </>
                                    ) : (
                                        <span>Continue</span>
                                    )}
                                </motion.button>
                            </form>

                            <div className="mt-8 pt-5 border-t border-slate-100 flex items-center justify-center gap-2 text-xs text-slate-400 font-medium">
                                <FaLock className="text-slate-300" />
                                <span>256-bit SSL Bank Grade Encryption</span>
                            </div>
                        </motion.div>
                    ) : (
                        /* STEP 2: OTP VERIFICATION */
                        <motion.div
                            key="step-2-otp"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            transition={{ duration: 0.3 }}
                            className="w-full my-auto"
                        >
                            {/* Top info and back button */}
                            <div className="mb-6 flex items-start justify-between gap-4">
                                <div>
                                    <p className="text-xs sm:text-sm text-slate-500 mb-1 font-normal">
                                        OTP has been sent to
                                    </p>
                                    <div className="flex items-center gap-2">
                                        <span className="font-bold text-slate-900 text-sm sm:text-base break-all">
                                            {email}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => !loading && setStep(1)}
                                            disabled={loading}
                                            aria-label="Change email address"
                                            className="text-xs text-[#0f766e] hover:text-[#14b8a6] font-semibold underline underline-offset-2 transition-colors disabled:opacity-50"
                                        >
                                            Change
                                        </button>
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    onClick={() => !loading && setStep(1)}
                                    disabled={loading}
                                    aria-label="Go back to email step"
                                    className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors flex items-center justify-center disabled:opacity-40"
                                >
                                    <FaArrowLeft className="text-sm" />
                                </button>
                            </div>

                            {/* Bootstrap Alerts for Error Handling */}
                            {error && (
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="mb-5"
                                >
                                    <Alert
                                        variant="danger"
                                        onClose={() => setError(null)}
                                        dismissible
                                        className="rounded-2xl border border-red-200 bg-red-50 text-red-900 p-4 shadow-sm"
                                    >
                                        <div className="flex items-start gap-3 pr-2">
                                            <FaExclamationCircle className="text-red-600 mt-0.5 text-lg flex-shrink-0" />
                                            <div>
                                                <div className="font-bold text-sm">{error.type}</div>
                                                <div className="text-xs sm:text-sm mt-0.5 text-red-700">
                                                    {error.message}
                                                </div>
                                            </div>
                                        </div>
                                    </Alert>
                                </motion.div>
                            )}

                            <form onSubmit={handleVerifyOTP} className="space-y-6">
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">
                                        Enter 6-Digit Verification Code
                                    </label>

                                    {/* 6 Individual OTP Boxes using react-otp-input */}
                                    <div className="w-full flex justify-center">
                                        <OtpInput
                                            value={otp}
                                            onChange={(val) => {
                                                // Accept numbers only
                                                const numericOnly = val.replace(/[^0-9]/g, "");
                                                setOtp(numericOnly);
                                            }}
                                            numInputs={6}
                                            shouldAutoFocus={true}
                                            inputType="tel"
                                            renderInput={(inputProps) => (
                                                <motion.input
                                                    {...inputProps}
                                                    whileFocus={{
                                                        scale: 1.05,
                                                        borderColor: "#000000",
                                                        boxShadow: "0 0 0 4px rgba(46, 46, 46, 0.22)"
                                                    }}
                                                    disabled={loading}
                                                    aria-label="Verification digit"
                                                    className="!w-10 sm:!w-12 md:!w-13 !h-12 sm:!h-14 text-center text-lg sm:text-2xl font-bold rounded-xl sm:rounded-2xl border-2 border-slate-200 bg-slate-50/90 text-slate-900 focus:bg-white focus:outline-none transition-colors mx-1 sm:mx-1.5 selection:bg-teal-100 selection:text-teal-900"
                                                />
                                            )}
                                            containerStyle="flex justify-center w-full"
                                        />
                                    </div>
                                </div>

                                {/* COUNTDOWN & RESEND SECTION */}
                                <div className="flex items-center justify-between text-sm py-2 px-1 bg-slate-50/80 rounded-xl border border-slate-100">
                                    <span className="text-slate-500 font-medium text-xs sm:text-sm pl-2">
                                        Didn't receive code?
                                    </span>

                                    {timeLeft > 0 ? (
                                        <div className="flex items-center gap-1.5 font-bold text-amber-600 bg-amber-50 px-3 py-1 rounded-lg border border-amber-200/60 text-xs sm:text-sm">
                                            <FaSpinner className="animate-spin text-xs opacity-75" />
                                            <span>Expires in {formatTime(timeLeft)}</span>
                                        </div>
                                    ) : (
                                        <motion.button
                                            type="button"
                                            whileHover={{ scale: 1.03 }}
                                            whileTap={{ scale: 0.97 }}
                                            onClick={handleResendOTP}
                                            disabled={resending || loading}
                                            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#0f766e]/10 text-[#0f766e] hover:bg-[#0f766e]/20 font-bold text-xs sm:text-sm transition-colors disabled:opacity-50"
                                        >
                                            <FaRedoAlt className={resending ? "animate-spin text-xs" : "text-xs"} />
                                            <span>{resending ? "Resending..." : "Resend OTP"}</span>
                                        </motion.button>
                                    )}
                                </div>

                                {/* VERIFY BUTTON */}
                                <motion.button
                                    type="submit"
                                    disabled={loading || otp.length < 6}
                                    whileHover={loading || otp.length < 6 ? {} : { scale: 1.015 }}
                                    whileTap={loading || otp.length < 6 ? {} : { scale: 0.985 }}
                                    className="w-full py-4 px-6 rounded-2xl bg-black hover:from-[#0d645d] hover:to-[#0f9f8f] text-white font-bold text-base shadow-lg shadow-teal-700/25 transition-all duration-200 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-4 focus:ring-teal-500/30"
                                >
                                    {loading ? (
                                        <>
                                            <FaSpinner className="animate-spin text-xl text-white" />
                                            <span>Verifying OTP...</span>
                                        </>
                                    ) : (
                                        <span>Verify & Continue Booking</span>
                                    )}
                                </motion.button>
                            </form>

                            <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-400">
                                <span>Enter ESC to close</span>
                                <span className="flex items-center gap-1">
                                    <FaShieldAlt /> Secured by GoSehari Pay
                                </span>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </Modal.Body>
        </Modal>
    );
}
